import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { CustomerDetail, CustomerLifetimeLicense } from '@/lib/api/types'
import { formatDate, formatUsd } from '@/lib/format'
import { useLifetimeAction } from './api'

/** The tiers, for the internal test licence (config/plans.php 'lifetime'). */
const TIERS = [
  { key: 'lifetime_2', name: 'Lifetime – 2 websites' },
  { key: 'lifetime_5', name: 'Lifetime – 5 websites' },
  { key: 'lifetime_12', name: 'Lifetime – 12 websites' },
]

const ENDED: Record<string, string> = {
  refunded: 'Refunded',
  withdrawn: 'Withdrawn (14-day refund)',
  disputed: 'Disputed (chargeback)',
  discounted: 'Refunded: discounted order',
  base_ended: 'Ended with the purchase it built on',
  closed: 'Workspace closed',
  revoked: 'Revoked',
  change_refund: 'Change refund',
  closure_refund: 'Closure refund',
}

const SOURCE: Record<CustomerLifetimeLicense['source'], string> = {
  order: 'Paid order',
  attached: 'Paid order, attached by staff',
  internal: 'Internal test licence',
}

type Open =
  | { kind: 'attach' }
  | { kind: 'internal' }
  | { kind: 'revoke'; licence: CustomerLifetimeLicense }
  | { kind: 'move'; licence: CustomerLifetimeLicense }
  | { kind: 'refund'; licence: CustomerLifetimeLicense; refund: 'change' | 'closure' }

/**
 * The customer's lifetime plan in the billing tab (decision 30): every payment, and the staff
 * actions. No free lifetime grants to customers: a complimentary plan with an end date does that.
 */
export function LifetimeCard({ customer: c }: { customer: CustomerDetail }) {
  const [open, setOpen] = useState<Open | null>(null)
  const lt = c.lifetime
  if (!lt) return null
  const base = lt.licenses.find((l) => l.kind === 'purchase' && l.status === 'active') ?? null
  if (lt.licenses.length === 0 && !lt.can.attach && !lt.can.internal) return null

  return (
    <Card>
      <CardHeader
        title="Lifetime plan"
        description={
          lt.plan
            ? `${lt.plan.name}: paid once. Refunds and revokes free a place in the batch; a closed workspace keeps its place.`
            : 'No lifetime plan. Attach a paid order whose payment message never arrived; there are no free lifetime plans for customers (give a complimentary plan with an end date instead).'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {lt.can.attach && (
              <Button size="sm" variant="secondary" onClick={() => setOpen({ kind: 'attach' })}>
                Attach a paid order
              </Button>
            )}
            {lt.can.internal && (
              <Button size="sm" variant="secondary" onClick={() => setOpen({ kind: 'internal' })}>
                Internal test licence
              </Button>
            )}
          </div>
        }
      />
      <CardBody className="grid gap-3 text-[13px]">
        {lt.licenses.length === 0 ? (
          <p className="text-ink-subtle">None yet.</p>
        ) : (
          <ul className="grid divide-y divide-border" aria-label="Lifetime payments">
            {lt.licenses.map((l) => (
              <li key={l.id} className="grid gap-1 py-2.5">
                <span className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-ink">{l.description}</span>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={l.status === 'active' ? 'success' : 'neutral'}>
                      {l.status === 'active' ? 'Active' : (ENDED[l.ended_reason ?? ''] ?? 'Ended')}
                    </Badge>
                    {l.test_mode && <Badge tone="outline">Test</Badge>}
                  </span>
                </span>
                <span className="text-xs text-ink-subtle">
                  {SOURCE[l.source]}
                  {l.provider_order_id && ` #${l.provider_order_id}`}
                  {l.source !== 'internal' &&
                    ` · ${formatUsd(l.price_paid)} before tax, ${formatUsd(l.total_paid)} with tax`}
                  {l.refunded > 0 && ` · ${formatUsd(l.refunded)} refunded`}
                  {` · ${formatDate(l.started_at)}`}
                  {l.withdrawable && l.refund_until && ` · full refund until ${formatDate(l.refund_until)}`}
                  {l.customer_email && ` · ${l.customer_email}`}
                </span>
                {(l.note || l.ended_note || l.moved_at) && (
                  <span className="text-xs text-ink-muted">
                    {l.note && `Note: ${l.note}. `}
                    {l.ended_note && `Ended: ${l.ended_note}. `}
                    {l.moved_at && `Moved here on ${formatDate(l.moved_at)}.`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {base && (lt.can.revoke || lt.can.move || lt.can.refund) && (
          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            {lt.can.move && (
              <Button size="sm" variant="secondary" onClick={() => setOpen({ kind: 'move', licence: base })}>
                Move to another workspace
              </Button>
            )}
            {lt.can.refund && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setOpen({ kind: 'refund', licence: base, refund: 'change' })}
                >
                  Change refund
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setOpen({ kind: 'refund', licence: base, refund: 'closure' })}
                >
                  Closure refund
                </Button>
              </>
            )}
            {lt.can.revoke && (
              <Button size="sm" variant="danger" onClick={() => setOpen({ kind: 'revoke', licence: base })}>
                Revoke
              </Button>
            )}
          </div>
        )}
        <p className="text-xs text-ink-subtle">
          Internal test licences used: {lt.internal_licences_used} of {lt.internal_licences_max}, ever. They
          count in the batch of 100.
        </p>
      </CardBody>
      {open && <ActionDialog customer={c} open={open} onClose={() => setOpen(null)} />}
    </Card>
  )
}

function ActionDialog({
  customer: c,
  open,
  onClose,
}: {
  customer: CustomerDetail
  open: Open
  onClose: () => void
}) {
  const action = useLifetimeAction(c.id)
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const [order, setOrder] = useState('')
  const [target, setTarget] = useState('')
  const [plan, setPlan] = useState('lifetime_2')
  const fieldError = (f: string) => (isApiError(action.error) ? action.error.firstFieldError(f) : undefined)
  const quote = c.lifetime?.refund_quote ?? null
  const done = (message: string) => ({
    onSuccess: () => {
      toast.success(message)
      onClose()
    },
    onError: (e: unknown) => toast.error(errorMessage(e)),
  })

  const copy = {
    attach: {
      title: 'Attach a paid order',
      description: `For a lifetime order paid on Lemon Squeezy whose payment message never reached Arva SEO. The order is read from Lemon Squeezy and must be paid and not refunded; ${c.name} gets its plan and the confirmation email.`,
      button: 'Attach order',
    },
    internal: {
      title: 'Internal test licence',
      description: `For the team’s own checks only, never for a customer. It counts in the batch of 100, and at most ${c.lifetime?.internal_licences_max ?? 3} are ever made (revoked ones still count). Revoke it when the check is done.`,
      button: 'Create test licence',
    },
    revoke: {
      title: 'Revoke the lifetime plan',
      description:
        'Only when the customer broke the Terms or the Acceptable Use Policy and didn’t stop within 15 days of a warning, or at once for fraud, attacks on websites or illegal use. No refund. Its upgrades end with it, its place in the batch is freed, and the owner is told the reason.',
      button: 'Revoke',
    },
    move: {
      title: 'Move to another workspace',
      description:
        'Once only, to another open workspace the same person owns that has no lifetime plan (a sale of the business: change the owner instead). Its upgrades move with it.',
      button: 'Move',
    },
    refund: {
      title: open.kind === 'refund' && open.refund === 'closure' ? 'Closure refund' : 'Change refund',
      description:
        open.kind === 'refund' && open.refund === 'closure'
          ? 'When Arva SEO closes: the unused part of each payment’s first 36 months is refunded through Lemon Squeezy, on the price paid before tax, and the plan ends.'
          : 'When an allowance is lowered by more than 20% (or the law gives the right) and the customer chooses the refund: the unused part of each payment’s first 36 months is refunded, and the plan ends.',
      button: `Refund ${formatUsd(quote?.total_usd ?? 0)} and end the plan`,
    },
  }[open.kind]

  const submit = () => {
    const n = note.trim()
    switch (open.kind) {
      case 'attach': {
        action.mutate(
          { kind: 'attach', order_id: order.trim(), note: n },
          done('Order attached: the plan started.'),
        )
        return
      }
      case 'internal': {
        action.mutate({ kind: 'internal', plan, note: n }, done('Internal test licence created.'))
        return
      }
      case 'revoke': {
        action.mutate({ kind: 'revoke', license: open.licence.id, reason: n }, done('Lifetime plan revoked.'))
        return
      }
      case 'move': {
        action.mutate(
          { kind: 'move', license: open.licence.id, workspace_id: target.trim(), note: n },
          done('Lifetime plan moved.'),
        )
        return
      }
      case 'refund': {
        // Lemon Squeezy refusing a refund leaves the plan running and the customer untold: the
        // error lists the orders it refused, and the payments refunded so far show at once.
        action.mutate(
          { kind: 'refund', license: open.licence.id, refund: open.refund, note: n },
          {
            ...done('Refunded, and the plan ended.'),
            onError: (e: unknown) => {
              toast.error(errorMessage(e))
              void queryClient.invalidateQueries({ queryKey: ['customer', c.id] })
            },
          },
        )
        return
      }
    }
  }
  const ready =
    note.trim() !== '' &&
    (open.kind !== 'attach' || order.trim() !== '') &&
    (open.kind !== 'move' || target.trim().length === 26)

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={copy.title}
        description={copy.description}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button
              variant={open.kind === 'revoke' || open.kind === 'refund' ? 'danger' : 'primary'}
              loading={action.isPending}
              disabled={!ready}
              onClick={submit}
            >
              {copy.button}
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          {open.kind === 'attach' && (
            <Field
              label="Lemon Squeezy order number"
              htmlFor="lt-order"
              required
              error={fieldError('order_id')}
            >
              <Input id="lt-order" value={order} onChange={(e) => setOrder(e.target.value)} />
            </Field>
          )}
          {open.kind === 'internal' && (
            <Field label="Plan" htmlFor="lt-plan" required error={fieldError('plan')}>
              <NativeSelect id="lt-plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
                {TIERS.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          )}
          {open.kind === 'move' && (
            <Field
              label="Workspace ID"
              htmlFor="lt-target"
              required
              error={fieldError('workspace_id')}
              hint="From the other workspace’s page in the control panel (26 characters)."
            >
              <Input id="lt-target" value={target} onChange={(e) => setTarget(e.target.value)} />
            </Field>
          )}
          {open.kind === 'refund' && quote && (
            <ul className="grid gap-1 rounded-md bg-surface-sunken px-3 py-2 text-[13px]" aria-label="Refund">
              {quote.payments.map((p) => (
                <li key={p.id} className="flex justify-between gap-3">
                  <span className="text-ink-muted">
                    {p.description}: {formatUsd(p.paid)} paid, {p.months} months ago
                  </span>
                  <span className="font-medium text-ink">{formatUsd(p.refund)}</span>
                </li>
              ))}
            </ul>
          )}
          <Field
            label={open.kind === 'revoke' ? 'Reason' : 'Note'}
            htmlFor="lt-note"
            required
            error={fieldError(open.kind === 'revoke' ? 'reason' : 'note')}
            hint={open.kind === 'revoke' ? 'The owner is told this reason.' : 'Kept in the staff audit log.'}
          >
            <Input id="lt-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  )
}
